import { ReactElement, Suspense, useState } from 'react';
import { useSuspenseQuery } from '@apollo/client/react';
import { Button, Drawer, FullLoader, Pill } from '@sc/shared-ui';
import {
  ArrowDownTrayIcon,
  CalendarIcon,
  CreditCardIcon,
  HashtagIcon,
} from '@heroicons/react/24/outline';
import { InvoiceStatus } from '@sc/graphql';
import { GQL_QUERY_READ_INVOICE } from '../utility/queries';
import { CardBrandLogo } from '../components/CardBrandLogo';
import { PayInvoiceModal } from '../components/PayInvoice';

interface Props {
  open: boolean;
  uuid: string | null;
  onClose: () => void;
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(iso));
};

const formatGbp = (
  value: number | null | undefined,
  currency = 'GBP'
): string => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(value);
};

const STATUS_PILL: Record<InvoiceStatus, { text: string; colour: string }> = {
  [InvoiceStatus.paid]: { text: 'Paid', colour: '#3aa16e' },
  [InvoiceStatus.open]: { text: 'Open', colour: '#da892b' },
  [InvoiceStatus.draft]: { text: 'Draft', colour: '#5f656d' },
  [InvoiceStatus.uncollectible]: { text: 'Late', colour: '#cb3b3b' },
  [InvoiceStatus.void]: { text: 'Void', colour: '#5f656d' },
};

const formatBrand = (brand: string): string =>
  brand
    .split(/[\s_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');

const InvoiceDetail = ({ uuid }: { uuid: string }): ReactElement => {
  const { data } = useSuspenseQuery(GQL_QUERY_READ_INVOICE, {
    variables: { uuid },
  });

  const invoice = data.readInvoice;

  const [showPay, setShowPay] = useState(false);
  const stripe = invoice.stripeDetails;
  const status = STATUS_PILL[invoice.status];
  const currency = invoice.currency;

  return (
    <div className="flex flex-col grow">
      <div className="p-5 flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="size-12 rounded-full bg-primary-1000 border border-primary-400 flex items-center justify-center shrink-0">
            <CreditCardIcon className="size-6 text-primary-400" />
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="font-grotesque text-h3 font-semibold text-white">
                {invoice.description}
              </h2>
              <Pill
                size="small"
                outline
                colour={status.colour}
                text={status.text}
              />
            </div>
            <p className="text-sm text-text-low-priority mt-1">
              Invoice issue date {formatDate(invoice.createdAt)}
            </p>
          </div>
        </div>
      </div>
      <hr className="border-none h-px bg-grey-t-700" />
      <div className="p-5 grid grid-cols-2 gap-5">
        <div>
          <p className="text-xs text-text-low-priority">Due date</p>
          <p className="text-body-medium text-white mt-1 flex items-center gap-2">
            <CalendarIcon className="size-4 text-text-low-priority" />
            {formatDate(stripe?.dueDate ?? invoice.createdAt)}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-low-priority">Invoice number</p>
          <p className="text-body-medium text-white mt-1 flex items-center gap-2">
            <HashtagIcon className="size-4 text-text-low-priority" />
            {stripe?.number ?? '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-text-low-priority">Payment method</p>
          <div className="text-body-medium text-white mt-1 flex items-center gap-2">
            {stripe?.paymentMethodBrand ? (
              <>
                <CardBrandLogo
                  brand={stripe.paymentMethodBrand}
                  className="w-8 h-5 shrink-0"
                />
                {formatBrand(stripe.paymentMethodBrand)} ending in{' '}
                {stripe.paymentMethodLast4 ?? '••••'}
              </>
            ) : (
              <span className="text-text-low-priority">—</span>
            )}
          </div>
        </div>
      </div>
      <hr className="border-none h-px bg-grey-t-700" />
      <div className="p-5 space-y-3 grow">
        <h3 className="font-grotesque text-h3 font-semibold text-white">
          Cost breakdown
        </h3>
        <div className="flex items-center justify-between text-body-medium text-white">
          <span>{invoice.description}</span>
          <span className="font-semibold">
            {formatGbp(invoice.subtotal, currency)}
          </span>
        </div>
        <div className="flex items-center justify-between text-body-medium text-white">
          <span>Taxes</span>
          <span className="font-semibold">
            {formatGbp(invoice.tax, currency)}
          </span>
        </div>
        <hr className="border-none h-px bg-grey-t-700" />
        <div className="flex items-center justify-between">
          <span className="font-semibold text-white">Total</span>
          <span className="font-semibold text-white">
            {formatGbp(invoice.total, currency)}
          </span>
        </div>
      </div>
      <div className="p-5 border-t border-grey-t-700 flex items-center gap-3">
        {(invoice.status === InvoiceStatus.open ||
          invoice.status === InvoiceStatus.uncollectible) && (
          <>
            <Button
              button={{
                text: 'Pay now',
                type: 'primary',
                size: 'medium',
                onClick: () => setShowPay(true),
              }}
            />
            {showPay && (
              <PayInvoiceModal
                open
                invoiceUuid={uuid}
                total={invoice.total ?? null}
                onClose={() => setShowPay(false)}
              />
            )}
          </>
        )}
        <Button
          button={{
            text: 'Download invoice',
            type:
              invoice.status === InvoiceStatus.open ||
              invoice.status === InvoiceStatus.uncollectible
                ? 'basic'
                : 'primary',
            size: 'medium',
            IconStart: ArrowDownTrayIcon,
            disabled: !stripe?.invoicePdfUrl,
          }}
          link={
            stripe?.invoicePdfUrl
              ? {
                  href: stripe.invoicePdfUrl,
                  target: '_blank',
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};

export const InvoiceDetailDrawer = ({
  open,
  uuid,
  onClose,
}: Props): ReactElement => (
  <Drawer
    title="Invoice"
    open={open}
    setOpen={(next) => {
      if (!next) onClose();
    }}
  >
    <div className="w-150 max-w-[90vw] grow overflow-y-auto flex flex-col">
      {uuid && (
        <Suspense fallback={<FullLoader className="size-6 m-10" />}>
          <InvoiceDetail uuid={uuid} />
        </Suspense>
      )}
    </div>
  </Drawer>
);
